import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import Alert from '@mui/material/Alert';
import Tippy from '@tippyjs/react';
import { Card, Text } from 'v3/components';
import 'tippy.js/dist/tippy.css';
import RouteMachineMap from 'v3/components/Map/LeafletRouteMachine/Map';
import PointsOfInterest from './PointsOfInterest';
import DeletePointModal from './PointsOfInterest/Modal/deletePoint';
import TravelEditCard from './EditCard';
import RouteMachineAlerts from './RouteMachineAlerts';
import api from 'services/api';
import { useSnackbar } from 'v3/components/Snackbar';
import { UNDER_GRO_ANALYSIS_SLUG } from 'v3/utils/constants';
import * as Styled from './styles';
import {
  hasCoordinateChanges,
  reconcilePointsWithWaypoints,
  resolveInitialPoints,
} from './utils';
import {
  canEditPointByRoute,
  calculateTotalDistanceMeters,
  cloneValue,
  getBlockedPoints,
  getBlockedText,
  getConsolidatedWarnings,
  getHasAnyEditablePoint,
  getRouteWaypointsFromPoints,
  getWarningHeadline,
  hasMissingRequiredFields,
  hasInvalidDateFields,
  normalizeBusinessPoints,
  parseDateForInput,
} from './utils/routeMachineCard';
import { buildPointUpdatesFromReverseGeocode } from './utils/reverseGeocode';
import { useRouteMachineValidation } from './hooks/useRouteMachineValidation';
import {
  DEFAULT_COUNTRY,
  enrichPointCountry,
  findCountryOption,
  hasCountryValue,
} from './utils/country';
import {
  TollsPlazaCalculationCard,
  EMPTY_TOLLS,
} from './TollsPlazaCalculation';

export default function TravelRouteCard({
  points: externalPoints = [],
  externalTotalDistanceInKm = 0,
  externalLoading = false,
  showEditActions = false,
  alwaysEditable = false,
  showRouteErrors = false,
  routeErrorMessage: externalRouteErrorMessage = '',
  externalRouteErrorPointIds = [],
  externalResetChangesTrigger = 0,
  validateRetroactive = true,
  hasActiveSM = false,
  showStatusBlockWarning = false,
  load = null,
  loadCreated = null,
  loadCreatedGro = null,
  travelData = null,
  takerValue = null,
  tollsPlazaInfos = null,
  setTollsPlazaInfos,
  setTollsPlazaLoadingGro,
  calculateTollsBeforeEditing = true,
  canEditRoute: externalCanEditRoute = null,
  polylineRoute: polylineRouteProp = null,
  externalPolylineRoute = null,
  polylineWaypoints = null,
  polyline = null,
  onChangeAddress,
  onChangeRoute,
  onSavePoints,
  destinationsError = {},
}) {
  const incomingPolylineRoute = polylineRouteProp || externalPolylineRoute || polyline || null;

  const createPointIdentifier = useCallback((prefix = 'point') => {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return `${prefix}-${crypto.randomUUID()}`;
    }

    return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  }, []);

  const shouldAutoEnableEdit = alwaysEditable || !showEditActions;
  const [enableEdit, setEnableEdit] = useState(shouldAutoEnableEdit);
  const [internalSaving, setInternalSaving] = useState(false);
  const [points, setPoints] = useState(externalPoints && externalPoints.length > 0 ? externalPoints : []);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingPoint, setEditingPoint] = useState({});
  const [addAction, setAddAction] = useState('add');
  const [countryOptions, setCountryOptions] = useState([]);
  const [polylineEditable, setPolylineEditable] = useState(incomingPolylineRoute);
  const [polylineWaypointsEditable, setPolylineWaypointsEditable] = useState(polylineWaypoints);
  const [totalDistanceEditable, setTotalDistanceEditable] = useState(externalTotalDistanceInKm || 0);
  const [hasRouterError, setHasRouterError] = useState(false);
  const [hasNoRouteInRegion, setHasNoRouteInRegion] = useState(false);
  const [routeErrorPointIds, setRouteErrorPointIds] = useState([]);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [internalShowRouteErrors, setInternalShowRouteErrors] = useState(false);
  const [tollsPlazaLoading, setTollsPlazaLoading] = useState(false);
  const [isRouteRecalculating, setIsRouteRecalculating] = useState(false);
  const dragRequestVersionRef = useRef(0);
  const pointsRef = useRef(externalPoints && externalPoints.length > 0 ? externalPoints : []);
  const lastAutoSaveKeyRef = useRef('');
  const lastHandledExternalResetRef = useRef(0);
  const suppressAutoSaveOnceRef = useRef(false);
  const editSnapshotRef = useRef(null);
  const suppressNextAutoOpenRef = useRef(false);
  const routeTopRef = useRef(null);
  const pointsListRef = useRef(null);
  const formRef = useRef(null);
  const lastAddressEmitKeyRef = useRef('');
  const lastRouteEmitKeyRef = useRef('');
  const lastRouteCalculatedKeyRef = useRef('');
  const emptyRouteStreakRef = useRef(0);
  const lastExternalPointsSyncKeyRef = useRef('');
  const suppressNextRoutingStartRef = useRef(false);
  const [hasRouteChangedSinceEditStart, setHasRouteChangedSinceEditStart] = useState(
    !showEditActions || calculateTollsBeforeEditing,
  );

  useEffect(() => {
    api.get('countries').then(({ data }) => setCountryOptions(data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    const nextPoints =
      externalPoints && externalPoints.length > 0
        ? normalizeBusinessPoints(externalPoints)
        : [];
    const nextPointsSyncKey = JSON.stringify(nextPoints || []);

    if (nextPointsSyncKey === lastExternalPointsSyncKeyRef.current) return;

    lastExternalPointsSyncKeyRef.current = nextPointsSyncKey;
    setPoints(nextPoints);
  }, [externalPoints, normalizeBusinessPoints]);

  const snackbar = useSnackbar();
  useEffect(() => {
    pointsRef.current = points;
  }, [points]);

  useEffect(() => {
    if (showEditActions && enableEdit) return;
    setPolylineEditable(incomingPolylineRoute);
  }, [incomingPolylineRoute, showEditActions, enableEdit]);

  useEffect(() => {
    if (showEditActions && enableEdit) return;
    setPolylineWaypointsEditable(polylineWaypoints);
  }, [polylineWaypoints, showEditActions, enableEdit]);

  useEffect(() => {
    setTotalDistanceEditable(externalTotalDistanceInKm || 0);
  }, [externalTotalDistanceInKm]);

  useEffect(() => {
    if (externalResetChangesTrigger === 0) return;
    if (lastHandledExternalResetRef.current === externalResetChangesTrigger) return;

    lastHandledExternalResetRef.current = externalResetChangesTrigger;
    suppressAutoSaveOnceRef.current = true;

    setPoints(normalizeBusinessPoints(resolveInitialPoints(externalPoints)));
    setPolylineEditable(incomingPolylineRoute);
    setPolylineWaypointsEditable(polylineWaypoints);
    setTotalDistanceEditable(externalTotalDistanceInKm || 0);
    setHasRouterError(false);
    setHasNoRouteInRegion(false);
    setRouteErrorPointIds([]);
    setInternalShowRouteErrors(false);
    setIsRouteRecalculating(false);
    setEnableEdit(shouldAutoEnableEdit);
    setShowAddForm(false);
    setEditingPoint({});
    setConfirmDeleteId(null);
    lastAutoSaveKeyRef.current = '';
  }, [
    externalResetChangesTrigger,
    externalTotalDistanceInKm,
    externalPoints,
    incomingPolylineRoute,
    polylineWaypoints,
    normalizeBusinessPoints,
    shouldAutoEnableEdit,
  ]);

  useEffect(() => {
    if (!shouldAutoEnableEdit) return;
    setEnableEdit(true);
  }, [shouldAutoEnableEdit]);

  const scrollToRouteTop = useCallback(() => {
    if (!routeTopRef.current) return;

    window.setTimeout(() => {
      routeTopRef.current?.scrollIntoView?.({
        behavior: 'smooth',
        block: 'start',
      });
    }, 100);
  }, []);

  const scrollToForm = useCallback((attempt = 0) => {
    const formElement = formRef.current;

    if (formElement?.scrollIntoView) {
      window.requestAnimationFrame(() => {
        formElement.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      });
      return;
    }

    if (attempt >= 8) return;

    window.setTimeout(() => {
      scrollToForm(attempt + 1);
    }, 80);
  }, []);


  const isViaPoint = useCallback(
    point => point?.type?.id === 'via' || point?.action?.id === 'via',
    [],
  );

  const getBusinessPoints = useCallback(
    pointsList => (pointsList || []).filter(point => !isViaPoint(point)),
    [isViaPoint],
  );

  const visiblePoints = points.filter(
    point => !isViaPoint(point),
  );
  const hasEnoughPointsForRoute = visiblePoints.length >= 2;
  const isBlockedByRisk =
    load?.travel?.riskApproved === UNDER_GRO_ANALYSIS_SLUG;
  const isBlockedByWaitingIssuing =
    load?.travel?.travelStatus?.code === 'ST9';
  const routeEditingAllowed =
    externalCanEditRoute !== null ? externalCanEditRoute : !isBlockedByRisk;
  const canManipulateMap = shouldAutoEnableEdit || enableEdit;
  const canEditRoute = canManipulateMap && routeEditingAllowed;
  const canEditMapPoints =
    canManipulateMap &&
    routeEditingAllowed &&
    (!showEditActions || enableEdit);
  const hasOrigin = Boolean(visiblePoints?.[0]?.lat && visiblePoints?.[0]?.lng);
  const hasDestination = visiblePoints
    .slice(1)
    .some(
      point =>
        typeof point?.lat === 'number' &&
        !Number.isNaN(point.lat) &&
        typeof point?.lng === 'number' &&
        !Number.isNaN(point.lng),
    );
  const missingBoth = !hasOrigin && !hasDestination;
  const missingOnlyDestination = hasOrigin && !hasDestination;
  const missingOnlyOrigin = !hasOrigin && hasDestination;
  const hasRouteLevelError =
    (showRouteErrors || internalShowRouteErrors) &&
    (missingBoth || missingOnlyDestination || missingOnlyOrigin);
  const routeErrorMessage = externalRouteErrorMessage || 'Verifique os campos obrigatórios.*';
  const shouldMaskTollsWhileRecalculating =
    enableEdit && hasRouteChangedSinceEditStart && isRouteRecalculating && !polylineEditable;
  const effectiveTollsPlazaInfos = hasEnoughPointsForRoute
    ? (shouldMaskTollsWhileRecalculating ? EMPTY_TOLLS : tollsPlazaInfos)
    : EMPTY_TOLLS;

  const handleRoutingStateChange = useCallback(({ status }) => {
    const shouldSkipPreEditRecalculation =
      !calculateTollsBeforeEditing && !enableEdit;
    const shouldKeepCurrentTollsWhileOpeningEdit =
      enableEdit && !hasRouteChangedSinceEditStart;

    if (status === 'start') {
      if (suppressNextRoutingStartRef.current) {
        suppressNextRoutingStartRef.current = false;
        return;
      }

      if (shouldSkipPreEditRecalculation) {
        return;
      }

      if (shouldKeepCurrentTollsWhileOpeningEdit) {
        return;
      }

      setIsRouteRecalculating(true);

      if (!polylineEditable && hasEnoughPointsForRoute && typeof setTollsPlazaInfos === 'function') {
        setTollsPlazaInfos({ ...EMPTY_TOLLS });
      }

      return;
    }

    if (status === 'error') {
      setIsRouteRecalculating(false);
    }
  }, [
    calculateTollsBeforeEditing,
    enableEdit,
    hasEnoughPointsForRoute,
    hasRouteChangedSinceEditStart,
    incomingPolylineRoute,
    polylineEditable,
    setTollsPlazaInfos,
  ]);

  const emitAddressChange = useCallback((nextPoints = [], nextPolylineRoute = polylineEditable, options = {}) => {
    if (typeof onChangeAddress !== 'function') return;

    const normalizedPoints = (nextPoints || []).filter(Boolean);
    const businessPoints = getBusinessPoints(normalizedPoints);
    const [origin, ...destinations] = businessPoints;
    const routeWaypoints = Object.prototype.hasOwnProperty.call(options, 'polylineWaypoints')
      ? options.polylineWaypoints
      : getRouteWaypointsFromPoints(normalizedPoints);
    const emitKey = JSON.stringify({
      origin,
      destinations,
      polyline: nextPolylineRoute || null,
      polylineWaypoints: routeWaypoints,
    });

    if (emitKey === lastAddressEmitKeyRef.current) return;
    lastAddressEmitKeyRef.current = emitKey;

    onChangeAddress({
      origin: origin || null,
      destinations: destinations || [],
      polyline: nextPolylineRoute || null,
      polylineWaypoints: routeWaypoints,
    });
  }, [getBusinessPoints, onChangeAddress, polylineEditable]);

  const syncDraggedPointAddresses = useCallback(({
    sourcePoints = [],
    nextPoints = [],
    requestVersion,
  }) => {
    buildPointUpdatesFromReverseGeocode({
      sourcePoints,
      nextPoints,
      countryOptions,
      findCountryOption,
      hasCountryValue,
      defaultCountry: DEFAULT_COUNTRY,
    }).then((results) => {
      if (requestVersion !== dragRequestVersionRef.current) return;

      const updates = results.filter(Boolean);
      if (updates.length === 0) return;

      const updatesByKey = new Map(updates.map(update => [update.pointKey, update]));

      setPoints((previousPoints) => {
        const updatedPoints = previousPoints.map((point, i) => {
          const pointKey = point?.identifier || point?.id || `idx-${i}`;
          const update = updatesByKey.get(pointKey);
          return update ? { ...point, ...update.fields } : point;
        });

        pointsRef.current = updatedPoints;
        emitAddressChange(updatedPoints, polylineEditable);

        return updatedPoints;
      });

      setEditingPoint((previousEditingPoint) => {
        if (!previousEditingPoint?.identifier) return previousEditingPoint;

        const editingPointKey = previousEditingPoint?.identifier || previousEditingPoint?.id;
        if (!editingPointKey) return previousEditingPoint;

        const update = updatesByKey.get(editingPointKey);
        if (!update) return previousEditingPoint;

        return {
          ...previousEditingPoint,
          ...update.fields,
        };
      });
    });
  }, [
    countryOptions,
    emitAddressChange,
    findCountryOption,
    hasCountryValue,
    polylineEditable,
  ]);

  useEffect(() => {
    if (hasOrigin && hasDestination) {
      setInternalShowRouteErrors(false);
    }
  }, [hasOrigin, hasDestination]);

  useEffect(() => {
    if (!showAddForm || !canEditRoute) return;
    scrollToForm();
  }, [showAddForm, addAction, canEditRoute, scrollToForm]);

  useEffect(() => {
    if (hasEnoughPointsForRoute) return;
    if (typeof setTollsPlazaInfos !== 'function') return;

    setIsRouteRecalculating(false);
    setTollsPlazaInfos({ ...EMPTY_TOLLS });
  }, [hasEnoughPointsForRoute, setTollsPlazaInfos]);

  const validateMachineRoute = useRouteMachineValidation({ visiblePoints, snackbar });

  const canEditPoint = useCallback(
    point => canEditPointByRoute(point, canManipulateMap && routeEditingAllowed),
    [canManipulateMap, routeEditingAllowed],
  );

  const canDragMapPoint = useCallback(
    point => canEditMapPoints && canEditPoint(point),
    [canEditMapPoints, canEditPoint],
  );

  const blockedPoints = useMemo(() => getBlockedPoints(visiblePoints), [visiblePoints]);

  const hasAnyEditablePoint = useMemo(
    () => getHasAnyEditablePoint(visiblePoints),
    [visiblePoints],
  );

  const blockedText = useMemo(
    () => getBlockedText(blockedPoints, visiblePoints),
    [blockedPoints, visiblePoints],
  );

  const consolidatedWarnings = useMemo(
    () => getConsolidatedWarnings({
      showStatusBlockWarning,
      isBlockedByRisk,
      visiblePoints,
      hasRouterError,
      hasNoRouteInRegion,
    }),
    [
      showStatusBlockWarning,
      isBlockedByRisk,
      visiblePoints,
      hasRouterError,
      hasNoRouteInRegion,
    ],
  );

  const warningHeadline = useMemo(
    () => getWarningHeadline(consolidatedWarnings),
    [consolidatedWarnings],
  );

  const mergedRouteErrorPointIds = useMemo(
    () => Array.from(new Set([...(routeErrorPointIds || []), ...(externalRouteErrorPointIds || [])])),
    [routeErrorPointIds, externalRouteErrorPointIds],
  );

  const shouldShowEditActions = showEditActions && !showStatusBlockWarning && !isBlockedByWaitingIssuing && (
    enableEdit || externalCanEditRoute !== false
  );

  useEffect(() => {
    if (showEditActions) return;
    if (!canEditRoute) return;
    if (showAddForm) return;

    if (suppressNextAutoOpenRef.current) {
      suppressNextAutoOpenRef.current = false;
      return;
    }

    const firstInvalidPoint = visiblePoints.find(
      point =>
        canEditPoint(point) &&
        (hasMissingRequiredFields(point) || hasInvalidDateFields(point)),
    );

    if (!firstInvalidPoint) return;

    let isCancelled = false;

    enrichPointCountry(firstInvalidPoint, countryOptions).then(enrichedPoint => {
      if (isCancelled) return;
      const pointToEdit = enrichedPoint || firstInvalidPoint;
      setEditingPoint({
        ...pointToEdit,
        start_schedule: parseDateForInput(pointToEdit?.start_schedule),
        scheduled_time: parseDateForInput(pointToEdit?.scheduled_time),
      });
      setAddAction('edit');
      setShowAddForm(true);
      scrollToForm();
    });

    return () => {
      isCancelled = true;
    };
  }, [
    canEditRoute,
    showAddForm,
    visiblePoints,
    canEditPoint,
    countryOptions,
    hasMissingRequiredFields,
    hasInvalidDateFields,
  ]);

  useEffect(() => {
    if (!alwaysEditable || typeof onSavePoints !== 'function') return;
    if (isRouteRecalculating) return;
    if (suppressAutoSaveOnceRef.current) {
      suppressAutoSaveOnceRef.current = false;
      return;
    }
    if (visiblePoints.length < 2) {
      const autoSaveKey = JSON.stringify({
        points,
        polylineRoute: null,
        polylineWaypoints: null,
        totalDistanceInKm: 0,
      });

      if (autoSaveKey === lastAutoSaveKeyRef.current) return;
      lastAutoSaveKeyRef.current = autoSaveKey;

      onSavePoints(points, { polylineRoute: null, polylineWaypoints: null, totalDistanceInKm: 0 });
      return;
    }

    if (!validateMachineRoute({ showErrors: false })) return;

    const autoSaveKey = JSON.stringify({
      points,
      polylineRoute: polylineEditable || null,
      polylineWaypoints: polylineWaypointsEditable || null,
      totalDistanceInKm: totalDistanceEditable || 0,
    });

    if (autoSaveKey === lastAutoSaveKeyRef.current) return;
    lastAutoSaveKeyRef.current = autoSaveKey;

    onSavePoints(points, {
      polylineRoute: polylineEditable || null,
      polylineWaypoints: polylineWaypointsEditable || null,
      totalDistanceInKm: totalDistanceEditable || 0,
    });
  }, [
    alwaysEditable,
    onSavePoints,
    points,
    polylineEditable,
    polylineWaypointsEditable,
    isRouteRecalculating,
    totalDistanceEditable,
    validateMachineRoute,
    visiblePoints.length,
  ]);

  const isFirstPoint = visiblePoints.length === 0;

  function handleStartEdit() {
    if (isBlockedByRisk) return;
    if (externalCanEditRoute === false) return;

    editSnapshotRef.current = {
      points: cloneValue(points),
      polylineEditable: polylineEditable || null,
      polylineWaypointsEditable: cloneValue(polylineWaypointsEditable),
      totalDistanceInKm: totalDistanceEditable || 0,
      tollsPlazaInfos: cloneValue(tollsPlazaInfos),
    };

    setPolylineEditable(incomingPolylineRoute);
    setPolylineWaypointsEditable(polylineWaypoints);
    if (!calculateTollsBeforeEditing && incomingPolylineRoute) {
      suppressNextRoutingStartRef.current = true;
    }
    setHasRouteChangedSinceEditStart(false);
    setEnableEdit(true);
  }

  const savingRef = useRef(false);
  async function handleSaveChanges() {
    if (isBlockedByRisk) return;
    if (typeof onSavePoints !== 'function') return;
    if (internalSaving || savingRef.current) return; // bloqueia múltiplos cliques
    if (!validateMachineRoute({ showErrors: true })) {
      setInternalShowRouteErrors(true);
      return;
    }
    setInternalSaving(true);
    savingRef.current = true;
    try {
      const saveResult = await onSavePoints(points, {
        polylineRoute: polylineEditable || null,
        polylineWaypoints: polylineWaypointsEditable || null,
        totalDistanceInKm: totalDistanceEditable || 0,
      });
      if (saveResult === false) {
        handleCancelChanges();
        return;
      }
      if (saveResult !== true) return;
      editSnapshotRef.current = null;
      setInternalShowRouteErrors(false);
      setIsRouteRecalculating(false);
      setHasRouteChangedSinceEditStart(!showEditActions || calculateTollsBeforeEditing);
      if (alwaysEditable) return;
      setEnableEdit(false);
      setShowAddForm(false);
      setEditingPoint({});
      scrollToRouteTop();
    } finally {
      setInternalSaving(false);
      savingRef.current = false;
    }
  }

  function handleCancelChanges() {
    suppressNextAutoOpenRef.current = true;

    const snapshot = editSnapshotRef.current;

    if (snapshot) {
      setPoints(normalizeBusinessPoints(resolveInitialPoints(snapshot.points || [])));
      setPolylineEditable(snapshot.polylineEditable || null);
      setPolylineWaypointsEditable(snapshot.polylineWaypointsEditable || null);
      setTotalDistanceEditable(snapshot.totalDistanceInKm || 0);

      if (typeof setTollsPlazaInfos === 'function') {
        setTollsPlazaInfos(snapshot.tollsPlazaInfos || null);
      }
    } else {
      setPoints(normalizeBusinessPoints(resolveInitialPoints(externalPoints)));
      setPolylineEditable(incomingPolylineRoute);
      setPolylineWaypointsEditable(polylineWaypoints);
      setTotalDistanceEditable(0);
    }

    setHasRouterError(false);
    setHasNoRouteInRegion(false);
    setRouteErrorPointIds([]);
    setInternalShowRouteErrors(false);
    setHasRouteChangedSinceEditStart(!showEditActions || calculateTollsBeforeEditing);
    editSnapshotRef.current = null;
    setEditingPoint({});
    setShowAddForm(false);
    if (alwaysEditable) return;
    setEnableEdit(false);
    scrollToRouteTop();
  }

  function handleAdd() {
    if (isBlockedByRisk) return;
    setEditingPoint({});
    setAddAction('add');
    setShowAddForm(true);
    scrollToForm();
  }

  async function handleEdit(point) {
    if (isBlockedByRisk) return;
    if (!canEditPoint(point)) return;
    const enrichedPoint = await enrichPointCountry(point, countryOptions);
    const pointToEdit = enrichedPoint || point;
    const nextEditingPoint = {
      ...pointToEdit,
      start_schedule: parseDateForInput(pointToEdit?.start_schedule),
      scheduled_time: parseDateForInput(pointToEdit?.scheduled_time),
    };

    setEditingPoint(nextEditingPoint);
    setAddAction('edit');
    setShowAddForm(true);
    scrollToForm();
  }

  function handleDelete(identifier) {
    if (isBlockedByRisk) return;

    const pointToDelete = points.find(point => point?.identifier === identifier);
    if (pointToDelete && !canEditPoint(pointToDelete)) return;

    setConfirmDeleteId(identifier);
  }

  function handleConfirmDelete() {
    if (!confirmDeleteId) return;

    const pointToDelete = points.find(point => point?.identifier === confirmDeleteId);
    const shouldResetRouteWaypoints = pointToDelete ? !isViaPoint(pointToDelete) : true;
    let nextPoints = points.filter(point => point?.identifier !== confirmDeleteId);
    const nextVisiblePoints = getBusinessPoints(nextPoints);

    if (showEditActions && enableEdit && nextVisiblePoints.length === 0) {
      setConfirmDeleteId(null);
      snackbar.show(
        <Text>É necessário manter pelo menos um ponto na rota.</Text>,
        { type: 'error' },
      );
      return;
    }

    if (shouldResetRouteWaypoints || nextVisiblePoints.length < 2) {
      nextPoints = nextVisiblePoints;
    }

    const normalizedNextPoints = normalizeBusinessPoints(nextPoints);
    setPoints(normalizedNextPoints);
    pointsRef.current = normalizedNextPoints;

    if (shouldResetRouteWaypoints) {
      setHasRouteChangedSinceEditStart(true);
      setPolylineEditable(null);
      setPolylineWaypointsEditable(null);
      setTotalDistanceEditable(0);
      setIsRouteRecalculating(nextVisiblePoints.length >= 2);
      if (typeof setTollsPlazaInfos === 'function') {
        setTollsPlazaInfos({ ...EMPTY_TOLLS });
      }
      emitAddressChange(normalizedNextPoints, null, { polylineWaypoints: null });
    } else {
      emitAddressChange(normalizedNextPoints, polylineEditable);
    }

    if (nextVisiblePoints.length < 2) {
      setPolylineEditable(null);
      setPolylineWaypointsEditable(null);
      setTotalDistanceEditable(0);
      setIsRouteRecalculating(false);
      setHasRouterError(false);
      setHasNoRouteInRegion(false);
      setRouteErrorPointIds([]);

      if (typeof setTollsPlazaInfos === 'function') {
        setTollsPlazaInfos({ ...EMPTY_TOLLS });
      }
    }

    setConfirmDeleteId(null);
  }

  async function handleSave(point, action) {
    if (isBlockedByRisk) return;
    const normalizedPoint = {
      ...point,
      identifier:
        point?.identifier ||
        point?.id ||
        createPointIdentifier(point?.type?.id === 'via' ? 'via' : 'point'),
    };
    const isSavedPointVia = isViaPoint(normalizedPoint);
    let shouldResetRouteWaypoints = false;

    const currentPoints = Array.isArray(pointsRef.current) ? pointsRef.current : points;
    let nextPointsAfterSave = [];

    if (action === 'edit') {
      const previousPoint = currentPoints.find(
        p => p.identifier === normalizedPoint.identifier,
      );
      const didBusinessPointCoordinateChange =
        !isSavedPointVia &&
        previousPoint &&
        (
          Number(previousPoint?.lat) !== Number(normalizedPoint?.lat) ||
          Number(previousPoint?.lng) !== Number(normalizedPoint?.lng)
        );
      shouldResetRouteWaypoints = didBusinessPointCoordinateChange;

      const basePoints = didBusinessPointCoordinateChange
        ? getBusinessPoints(currentPoints)
        : currentPoints;

      nextPointsAfterSave = normalizeBusinessPoints(basePoints.map((p) => (
        p.identifier === normalizedPoint.identifier ? normalizedPoint : p
      )));
    } else {
      shouldResetRouteWaypoints = !isSavedPointVia;
      const basePoints = shouldResetRouteWaypoints ? getBusinessPoints(currentPoints) : currentPoints;
      nextPointsAfterSave = normalizeBusinessPoints([...basePoints, normalizedPoint]);
    }

    setPoints(nextPointsAfterSave);
    pointsRef.current = nextPointsAfterSave;

    if (shouldResetRouteWaypoints) {
      const hasEnoughBusinessPointsAfterSave = getBusinessPoints(nextPointsAfterSave).length >= 2;
      setHasRouteChangedSinceEditStart(true);
      setPolylineEditable(null);
      setPolylineWaypointsEditable(null);
      setTotalDistanceEditable(0);
      setIsRouteRecalculating(hasEnoughBusinessPointsAfterSave);
      if (typeof setTollsPlazaInfos === 'function') {
        setTollsPlazaInfos({ ...EMPTY_TOLLS });
      }
    }

    emitAddressChange(
      nextPointsAfterSave,
      shouldResetRouteWaypoints ? null : polylineEditable,
      shouldResetRouteWaypoints ? { polylineWaypoints: null } : {},
    );

    setShowAddForm(false);
    setEditingPoint({});
    scrollToRouteTop();
  }

  function handleWaypointsChange(endpoints) {
    if (isBlockedByRisk) return;
    if (!canManipulateMap || !routeEditingAllowed) return;
    if (showEditActions && !enableEdit) return;
    if (!Array.isArray(endpoints) || endpoints.length < 2) return;

    dragRequestVersionRef.current += 1;
    const requestVersion = dragRequestVersionRef.current;
    const sourcePoints = Array.isArray(pointsRef.current) ? pointsRef.current : [];
    if (sourcePoints.length === 0) return;
    const sourceBusinessPoints = getBusinessPoints(sourcePoints);
    const didBusinessPointCoordinateChange =
      endpoints.length === sourcePoints.length &&
      sourcePoints.some((point, index) => {
        if (isViaPoint(point)) return false;

        const endpoint = endpoints[index];
        if (!endpoint) return false;

        return (
          Number(point?.lat) !== Number(endpoint?.lat) ||
          Number(point?.lng) !== Number(endpoint?.lng)
        );
      });
    const shouldResetIntermediateWaypoints = didBusinessPointCoordinateChange;

    if (shouldResetIntermediateWaypoints && sourceBusinessPoints.length >= 2) {
      setHasRouteChangedSinceEditStart(true);
      const nextPoints = normalizeBusinessPoints(
        sourcePoints
          .map((point, index) => {
            if (isViaPoint(point)) return null;

            const endpoint = endpoints[index];
            if (!endpoint) return point;

            return {
              ...point,
              lat: endpoint.lat,
              lng: endpoint.lng,
            };
          })
          .filter(Boolean),
      );

      setPoints(nextPoints);
      pointsRef.current = nextPoints;
      setEditingPoint((previousEditingPoint) => {
        if (!previousEditingPoint?.identifier) return previousEditingPoint;

        const updatedPoint = nextPoints.find(
          point => point?.identifier === previousEditingPoint.identifier,
        );

        return updatedPoint || previousEditingPoint;
      });
      setPolylineEditable(null);
      setPolylineWaypointsEditable(null);
      setTotalDistanceEditable(0);
      setIsRouteRecalculating(true);
      if (typeof setTollsPlazaInfos === 'function') {
        setTollsPlazaInfos({ ...EMPTY_TOLLS });
      }
      emitAddressChange(nextPoints, null);
      syncDraggedPointAddresses({
        sourcePoints,
        nextPoints,
        requestVersion,
      });
      return;
    }

    const getNextPoints = (previousPoints) => {
      if (previousPoints.length === 0) return previousPoints;
      const next = reconcilePointsWithWaypoints(previousPoints, endpoints);
      return hasCoordinateChanges(previousPoints, next) ? next : previousPoints;
    };

    setPoints(getNextPoints);

    setEditingPoint((prev) => {
      if (!prev?.identifier) return prev;
      const nextPoints = reconcilePointsWithWaypoints(sourcePoints, endpoints);
      const updatedPoint = nextPoints.find((point) => point.identifier === prev.identifier);
      return updatedPoint || prev;
    });

    const nextPoints = reconcilePointsWithWaypoints(sourcePoints, endpoints);
    pointsRef.current = nextPoints;
    const nextWaypoints = getRouteWaypointsFromPoints(nextPoints);
    setPolylineWaypointsEditable(nextWaypoints);
    setIsRouteRecalculating(true);
    emitAddressChange(nextPoints, polylineEditable, { polylineWaypoints: nextWaypoints });
    syncDraggedPointAddresses({
      sourcePoints,
      nextPoints,
      requestVersion,
    });
  }

  function handleRouteCalculated({ coordinates, encodedPolyline }) {
    const coordinatesLength = coordinates?.length || 0;
    const nextPolyline = encodedPolyline || null;
    const nextDistance = calculateTotalDistanceMeters(coordinates || []);
    const hasRoutePayload = Boolean(nextPolyline) && coordinatesLength >= 2;
    const previousPolyline = polylineEditable || incomingPolylineRoute || null;
    const didPolylineChange = (nextPolyline || null) !== previousPolyline;
    const currentBusinessPoints = getBusinessPoints(pointsRef.current || []);
    const hasEnoughBusinessPoints = currentBusinessPoints.length >= 2;

    if (!hasRoutePayload) {
      emptyRouteStreakRef.current += 1;

      const hasExistingRoute = Boolean(polylineEditable || incomingPolylineRoute);
      const shouldIgnoreTransientEmptyResult =
        hasEnoughBusinessPoints &&
        hasExistingRoute &&
        emptyRouteStreakRef.current < 2;

      if (shouldIgnoreTransientEmptyResult) {
        return;
      }
    } else {
      emptyRouteStreakRef.current = 0;
    }

    const routeCalculatedKey = `${nextPolyline || ''}|${nextDistance}|${coordinatesLength}`;
    if (routeCalculatedKey === lastRouteCalculatedKeyRef.current) {
      return;
    }
    lastRouteCalculatedKeyRef.current = routeCalculatedKey;

    const nextWaypoints = getRouteWaypointsFromPoints(pointsRef.current);
    if (didPolylineChange) {
      setHasRouteChangedSinceEditStart(true);
    }
    setIsRouteRecalculating(false);
    setTotalDistanceEditable(prev => (prev === nextDistance ? prev : nextDistance));
    setPolylineEditable(prev => (prev === nextPolyline ? prev : nextPolyline));
    setPolylineWaypointsEditable(prev => {
      const previousValue = JSON.stringify(prev || null);
      const nextValue = JSON.stringify(nextWaypoints || null);
      return previousValue === nextValue ? prev : nextWaypoints;
    });

    if (typeof onChangeRoute === 'function' && !showEditActions) {
      const routeEmitKey = JSON.stringify({
        polyline: nextPolyline,
        polylineWaypoints: nextWaypoints,
      });

      if (routeEmitKey === lastRouteEmitKeyRef.current) {
        emitAddressChange(pointsRef.current, nextPolyline);
        return;
      }

      lastRouteEmitKeyRef.current = routeEmitKey;
      onChangeRoute({
        polyline: nextPolyline,
        polylineWaypoints: nextWaypoints,
      });
    }

    emitAddressChange(pointsRef.current, nextPolyline);
  }

  const origins = visiblePoints.filter((p) => p?.type?.id === 'collect' || p?.action?.id === 'collect');
  const destinations = visiblePoints.filter(
    (p) => p?.type?.id !== 'collect' && p?.action?.id !== 'collect',
  );

  return (
    <Card
      bodyCardPadding="1rem"
      header={
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '20 rem',
            width: '100%',
          }}
        >
          <Text type="header" style={{ marginBottom: 0 }}>
            Rota da viagem
          </Text>

          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            {consolidatedWarnings.length > 0 && (
              <Tippy
                content={
                  <div style={{ maxWidth: '300px' }}>
                    {consolidatedWarnings.map((warning, index) => (
                      <div
                        key={`${warning}-${index}`}
                        style={{ marginBottom: index < consolidatedWarnings.length - 1 ? '0.5rem' : 0 }}
                      >
                        ⚠️ {warning}
                      </div>
                    ))}
                  </div>
                }
                placement="bottom"
              >
                <div style={{ cursor: 'pointer' }}>
                  <Alert
                    severity="warning"
                    style={{
                      fontSize: '0.9rem',
                      lineHeight: '1.4',
                      whiteSpace: 'nowrap',
                      padding: '0.5rem 1rem',
                      display: 'inline-flex',
                      alignItems: 'center',
                      margin: 0,
                      maxWidth: 'fit-content',
                    }}
                  >
                    <b>Atenção:</b>
                    <span style={{ marginLeft: '0.5rem' }}>{warningHeadline}</span>
                  </Alert>
                </div>
              </Tippy>
            )}

            {shouldShowEditActions && (
              <TravelEditCard
                showActions
                enableEdit={enableEdit}
                externalLoading={internalSaving || externalLoading || tollsPlazaLoading}
                disableStartEdit={
                  isBlockedByRisk ||
                  isBlockedByWaitingIssuing ||
                  externalCanEditRoute === false ||
                  !hasAnyEditablePoint
                }
                onStartEdit={handleStartEdit}
                onSaveChanges={handleSaveChanges}
                onCancelEdit={handleCancelChanges}
              />
            )}
          </div>
        </div>
      }
    >
      <RouteMachineAlerts
        isBlockedByRisk={isBlockedByRisk}
        hasActiveSM={hasActiveSM}
        hasRouterError={hasRouterError}
        hasNoRouteInRegion={hasNoRouteInRegion}
        blockedPoints={blockedPoints}
        blockedText={blockedText}
        isBlockedByWaitingIssuing={isBlockedByWaitingIssuing}
      />

      <div ref={routeTopRef} />
      <Styled.ContentGrid>
        <Styled.MapCol>
          <RouteMachineMap
            points={points}
            tollsPlazaInfos={effectiveTollsPlazaInfos}
            hideTolls={
              showAddForm ||
              (hasRouteChangedSinceEditStart && tollsPlazaLoading) ||
              shouldMaskTollsWhileRecalculating ||
              !hasEnoughPointsForRoute
            }
            onWaypointsChange={handleWaypointsChange}
            onRoutingStateChange={handleRoutingStateChange}
            onRouteStatus={({ hasRouteError, hasNoRouteInRegion, errorPointIds }) => {
              setHasRouterError(Boolean(hasRouteError));
              setHasNoRouteInRegion(Boolean(hasNoRouteInRegion));
              setRouteErrorPointIds(Array.isArray(errorPointIds) ? errorPointIds : []);
            }}
            onRouteCalculated={handleRouteCalculated}
            canDragPoint={canDragMapPoint}
            allowWaypointEditing={canEditMapPoints}
            readOnly={!canManipulateMap || !routeEditingAllowed}
            showReadOnlyOverlay={!canEditMapPoints && !showEditActions}
          />
        </Styled.MapCol>

        <Styled.SideCol>
          <PointsOfInterest
            points={visiblePoints}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onAdd={handleAdd}
            canEditPoint={canEditPoint}
            isSaving={externalLoading || tollsPlazaLoading}
            hasRouteLevelError={hasRouteLevelError}
            routeErrorMessage={routeErrorMessage}
            routeErrorPointIds={mergedRouteErrorPointIds}
            pointsListRef={pointsListRef}
            destinationsError={destinationsError}
          />
        </Styled.SideCol>
      </Styled.ContentGrid>

      {typeof setTollsPlazaInfos === 'function' && (
        <TollsPlazaCalculationCard
          tollsPlazaInfos={effectiveTollsPlazaInfos}
          setTollsPlazaInfos={setTollsPlazaInfos}
          enableEdit={enableEdit && (calculateTollsBeforeEditing || hasRouteChangedSinceEditStart)}
          polyline={polylineEditable}
          load={load}
          loadCreated={loadCreated}
          loadCreatedGro={loadCreatedGro}
          travelData={travelData}
          takerValue={takerValue}
          setTollsPlazaLoadingGro={setTollsPlazaLoadingGro}
          onLoadingChange={setTollsPlazaLoading}
          snackbar={snackbar}
        />
      )}

      <TravelEditCard
        showActions={false}
        enableEdit={enableEdit}
        externalLoading={externalLoading}
        onStartEdit={handleStartEdit}
        onSaveChanges={handleSaveChanges}
        onCancelEdit={handleCancelChanges}
        showForm={showAddForm}
        formMode={addAction}
        pointData={editingPoint}
        onPointChange={setEditingPoint}
        onPointSave={handleSave}
        canEditRoute={canEditRoute}
        countryOptions={countryOptions}
        isFirstPoint={isFirstPoint}
        origins={origins}
        destinations={destinations}
        pointsListRef={pointsListRef}
        formRef={formRef}
        validateRetroactive={validateRetroactive}
              />

      <DeletePointModal
        isOpen={Boolean(confirmDeleteId)}
        onCancel={() => setConfirmDeleteId(null)}
        onConfirm={handleConfirmDelete}
      />
    </Card>
  );
}
